import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import { ChoiceChip, NotAllowed, PageLoader } from '@components';
import {
  AppRoutes,
  SearchParams,
  hasPermission,
  shareAPI,
  shareContextAPI,
} from '@constants';
import { useAppSelector } from '@redux/hooks';
import { selectPermissions } from '@redux/accounts';
import { useTitle } from '@hooks';
import { PageTitle } from 'constants/pageTitle';
import { isEmail } from '@utils';
import { logEvent } from '@services';
import { buttonSizeSx, secondaryButtonSx } from 'theme/buttons';

type ShareScope = 'client' | 'position' | 'submission';

/** What `GET /api/share/context` answers: the three scopes with their counts as they stand now. */
interface IShareContext {
  client: { name: string | null; positions: number; submissions: number };
  position: { id: string; name: string; submissions: number } | null;
  submission: {
    id: string;
    candidateName: string;
    positionName: string | null;
  };
  previousRecipients: string[];
}

interface IShareResult {
  shared: { id: string; recipientEmail: string; expiresAt: string }[];
  submissions: number;
  scopeLabel: string;
}

const plural = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? '' : 's'}`;

const SECONDARY_SX = { ...buttonSizeSx.md, ...secondaryButtonSx } as const;

/**
 * A client contact passes candidate submissions to colleagues on their side. They name the
 * people and choose what to share; the server records who granted what and when, and each
 * recipient gets their own access instead of a forwarded link.
 */
export const SharePage: React.FC = () => {
  const navigate = useNavigate();
  const setPageTitle = useTitle();
  const [searchParams] = useSearchParams();
  const submissionId = searchParams.get(SearchParams.SUBMISSION);
  // The email's Share button lands here directly, before the account and its permissions
  // are known: the context is fetched once they are, and again when the account changes.
  const accountsLoading = useAppSelector((state) => state.accounts.loading);
  const selectedAccountId = useAppSelector((state) => state.accounts.selected);
  const canShare = hasPermission(useAppSelector(selectPermissions), 'H:R');

  const [context, setContext] = useState<IShareContext | null>(null);
  const [loadError, setLoadError] = useState<'forbidden' | 'missing' | null>(
    null,
  );
  const [recipients, setRecipients] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [scope, setScope] = useState<ShareScope>('submission');
  /** What is wrong with the address field: shown under it, where the typist is looking. */
  const [fieldError, setFieldError] = useState<string | null>(null);
  /** What went wrong with the share itself: shown by the Share button. */
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<IShareResult | null>(null);

  useEffect(() => {
    setPageTitle(PageTitle.SHARE);
  }, [setPageTitle]);

  useEffect(() => {
    if (!submissionId) {
      setLoadError('missing');

      return;
    }
    if (accountsLoading) return;
    if (!canShare) {
      setLoadError('forbidden');

      return;
    }
    let cancelled = false;
    setContext(null);
    setLoadError(null);
    axios
      .get<IShareContext>(shareContextAPI(submissionId), {
        withCredentials: true,
      })
      .then(({ data }) => {
        if (!cancelled) setContext(data);
      })
      .catch((e) => {
        if (!cancelled) {
          setLoadError(e?.response?.status === 403 ? 'forbidden' : 'missing');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [submissionId, accountsLoading, canShare, selectedAccountId]);

  const addRecipient = useCallback((value: string): boolean => {
    const email = value.trim().toLowerCase().replace(/[,;]$/, '');
    if (!email) return true;
    if (!isEmail(email)) {
      setFieldError(`"${email}" is not a valid email address.`);

      return false;
    }
    setFieldError(null);
    setRecipients((current) =>
      current.includes(email) ? current : [...current, email],
    );

    return true;
  }, []);

  const removeRecipient = useCallback((email: string) => {
    setRecipients((current) => current.filter((entry) => entry !== email));
  }, []);

  const candidatePath = useMemo(
    () => `/${AppRoutes.CANDIDATES}${submissionId ? `/${submissionId}` : ''}`,
    [submissionId],
  );

  const share = useCallback(async () => {
    if (!submissionId || !context) return;
    // Whatever is still typed counts too: people press Share before Enter.
    const pending = draft.trim();
    const list = pending
      ? addRecipient(pending)
        ? [...recipients, pending.toLowerCase().replace(/[,;]$/, '')]
        : null
      : recipients;
    if (!list) return;
    const unique = Array.from(new Set(list));
    if (!unique.length) {
      setFieldError('Add at least one email address.');

      return;
    }
    setFieldError(null);
    setError(null);
    setSubmitting(true);
    try {
      const { data } = await axios.post<IShareResult>(
        shareAPI,
        {
          submissionId,
          scope,
          scopeId: scope === 'position' ? context.position?.id : undefined,
          recipients: unique,
        },
        { withCredentials: true },
      );
      setDraft('');
      setResult(data);
      logEvent('submissions_shared', { scope, recipients: unique.length });
    } catch (e: any) {
      const message = e?.response?.data?.message;
      setError(
        (Array.isArray(message) ? message[0] : message) ||
          'We could not share right now. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }, [addRecipient, context, draft, recipients, scope, submissionId]);

  if (loadError === 'forbidden') return <NotAllowed />;

  if (loadError === 'missing') {
    return (
      <Stack px={{ xs: 2, tablet: 3 }} py={3} gap={2} maxWidth="48rem">
        <Typography variant="h2" component="h1">
          Share submissions
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {submissionId
            ? 'This candidate is not available to share.'
            : 'Open a candidate and choose Share to pass their submission to a colleague.'}
        </Typography>
        <Box>
          <Button
            component={RouterLink}
            to={`/${AppRoutes.CANDIDATES}`}
            variant="contained"
            sx={buttonSizeSx.md}
          >
            Go to candidates
          </Button>
        </Box>
      </Stack>
    );
  }

  if (!context) return <PageLoader />;

  if (result) {
    return (
      <Stack px={{ xs: 2, tablet: 3 }} py={3} gap={2} maxWidth="48rem">
        <Typography variant="h2" component="h1">
          Share submissions
        </Typography>
        <Paper elevation={5} sx={{ p: 3 }}>
          <Stack gap={1.5}>
            <Box display="flex" alignItems="center" gap={1}>
              <CheckCircleOutlineIcon color="success" />
              <Typography variant="h3" component="h2">
                Shared with{' '}
                {plural(result.shared.length, 'person').replace(
                  /persons$/,
                  'people',
                )}
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary">
              {plural(result.submissions, 'submission')}
              {scope === 'position' && ` for ${result.scopeLabel}`}
              {scope === 'client' && ' across every open position'}. Each person
              gets an email with their own links, good for 30 days. We recorded
              what you shared and with whom.
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {result.shared.map((grant) => (
                <Chip key={grant.id} label={grant.recipientEmail} />
              ))}
            </Box>
            <Box display="flex" gap={1.5} pt={1}>
              <Button
                component={RouterLink}
                to={candidatePath}
                variant="contained"
                sx={buttonSizeSx.md}
              >
                Back to the candidate
              </Button>
              <Button
                variant="outlined"
                sx={SECONDARY_SX}
                onClick={() => {
                  setResult(null);
                  setRecipients([]);
                }}
              >
                Share with more people
              </Button>
            </Box>
          </Stack>
        </Paper>
      </Stack>
    );
  }

  const options: {
    value: ShareScope;
    title: string;
    detail: string;
    disabled: boolean;
  }[] = [
    {
      value: 'client',
      title: `Everything open for ${context.client.name ?? 'your company'}`,
      detail: `${plural(context.client.submissions, 'submission')} across ${plural(context.client.positions, 'position')}`,
      disabled: context.client.submissions === 0,
    },
    {
      value: 'position',
      title: `Everything open for ${context.position?.name ?? 'this position'}`,
      detail: context.position
        ? plural(context.position.submissions, 'submission')
        : 'This position is not open right now',
      disabled: !context.position || context.position.submissions === 0,
    },
    {
      value: 'submission',
      title: 'This submission only',
      detail: [
        context.submission.candidateName,
        context.submission.positionName,
      ]
        .filter(Boolean)
        .join(' — '),
      disabled: false,
    },
  ];
  const previous = context.previousRecipients.filter(
    (email) => !recipients.includes(email),
  );
  const count = recipients.length + (draft.trim() ? 1 : 0);

  return (
    <Stack px={{ xs: 2, tablet: 3 }} py={3} gap={2} maxWidth="48rem">
      <Box>
        <Typography variant="h2" component="h1">
          Share submissions
        </Typography>
        <Typography variant="body1" color="text.secondary" mt={0.5}>
          Your colleagues get their own link and do not need an account.
        </Typography>
      </Box>

      <Paper elevation={5} sx={{ p: 3 }}>
        <Stack gap={2}>
          <Typography variant="h3" component="h2">
            Who are you sharing with?
          </Typography>
          <Autocomplete
            multiple
            freeSolo
            options={[] as string[]}
            value={recipients}
            inputValue={draft}
            onInputChange={(_event, value, reason) => {
              // MUI resets the input after a choice; a comma or a space after an address
              // adds it, the way mail clients do.
              if (reason === 'reset') {
                setDraft('');
              } else if (/[,;\s]$/.test(value) && addRecipient(value)) {
                setDraft('');
              } else {
                setDraft(value);
                setFieldError(null);
              }
            }}
            onChange={(_event, value) => {
              const typed = value.filter(
                (entry) => !recipients.includes(entry),
              );
              const kept = value.filter((entry) => recipients.includes(entry));
              setRecipients(kept);
              typed.forEach((entry) => addRecipient(entry));
              setDraft('');
            }}
            onBlur={() => {
              if (draft.trim() && addRecipient(draft)) setDraft('');
            }}
            renderTags={(value, getTagProps) =>
              value.map((email, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={email}
                  label={email}
                  onDelete={() => removeRecipient(email)}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                label="Email addresses"
                placeholder="Add an email address and press Enter"
                InputLabelProps={{ shrink: true }}
                error={Boolean(fieldError)}
                helperText={fieldError}
                FormHelperTextProps={{ role: 'alert' }}
                inputProps={{
                  ...params.inputProps,
                  'aria-label': 'Email addresses',
                }}
              />
            )}
          />
          {previous.length > 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" mb={1}>
                You have shared with these people before
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {previous.map((email) => (
                  <ChoiceChip
                    key={email}
                    selected={false}
                    onClick={() => addRecipient(email)}
                  >
                    {email}
                  </ChoiceChip>
                ))}
              </Box>
            </Box>
          )}
        </Stack>
      </Paper>

      <Paper elevation={5} sx={{ p: 3 }}>
        <Stack gap={2}>
          <Typography variant="h3" component="h2">
            What are you sharing?
          </Typography>
          <RadioGroup
            aria-label="What are you sharing"
            value={scope}
            onChange={(event) => setScope(event.target.value as ShareScope)}
          >
            <Stack gap={1.5}>
              {options.map((option) => (
                <Paper
                  key={option.value}
                  variant="outlined"
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderColor:
                      scope === option.value
                        ? 'text.link'
                        : 'background.dividerStrong',
                    backgroundColor:
                      scope === option.value
                        ? 'text.linkLight'
                        : 'common.white',
                    opacity: option.disabled ? 0.6 : 1,
                  }}
                >
                  <FormControlLabel
                    value={option.value}
                    disabled={option.disabled}
                    control={<Radio />}
                    sx={{
                      m: 0,
                      width: '100%',
                      alignItems: 'flex-start',
                      gap: 1,
                    }}
                    label={
                      <Box>
                        <Typography
                          variant="h5"
                          component="span"
                          display="block"
                        >
                          {option.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {option.detail}
                        </Typography>
                      </Box>
                    }
                  />
                </Paper>
              ))}
            </Stack>
          </RadioGroup>
          <Typography variant="body2" color="text.secondary">
            Only what is open today. Candidates submitted later are not included
            — share those when they arrive.
          </Typography>
        </Stack>
      </Paper>

      {error && (
        <Typography color="text.danger" variant="body2" role="alert">
          {error}
        </Typography>
      )}

      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        gap={1.5}
        position="sticky"
        bottom={0}
        py={1.5}
        bgcolor="background.backgroundPrimary"
      >
        <Button
          variant="contained"
          sx={buttonSizeSx.md}
          disabled={submitting || count === 0}
          onClick={() => void share()}
        >
          {count > 0
            ? `Share with ${plural(count, 'person').replace(/persons$/, 'people')}`
            : 'Share'}
        </Button>
        <Button
          variant="outlined"
          sx={SECONDARY_SX}
          onClick={() => navigate(candidatePath)}
        >
          Cancel
        </Button>
        <Typography variant="body2" color="text.secondary">
          Each person gets their own link, good for 30 days. We record what you
          shared and with whom.
        </Typography>
      </Box>
    </Stack>
  );
};
